import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateMarket } from '../../../hooks/useSDI'
import { X, Loader2 } from 'lucide-react'

const COUNTRIES = ['FR', 'DE', 'ES', 'IT', 'GB', 'US', 'BE', 'CH', 'CA', 'PT', 'NL']
const LANGUAGES = ['fr-FR', 'de-DE', 'es-ES', 'it-IT', 'en-GB', 'en-US', 'nl-NL', 'pt-PT']
const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'CAD']

interface Props { open: boolean; onClose: () => void }

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-ink-muted mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  )
}

export function MarketCreateModal({ open, onClose }: Props) {
  const navigate = useNavigate()
  const create = useCreateMarket()
  const [form, setForm] = useState({ name: '', country: 'FR', language: 'fr-FR', currency: 'EUR', seed_keyword: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})

  if (!open) return null

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim() || form.name.length < 2) e.name = 'Nom requis (min. 2 caractères)'
    if (!form.seed_keyword.trim()) e.seed_keyword = 'Mot-clé graine requis'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = () => {
    if (!validate()) return
    create.mutate(
      { name: form.name, country: form.country, language: form.language, currency: form.currency, seed_keyword: form.seed_keyword },
      {
        onSuccess: (data: Record<string, unknown>) => {
          onClose()
          navigate(`/sdi/markets/${data.id}/setup?onboarding=true`)
        },
      },
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-lg p-6 animate-slide-up">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-ink">Nouveau marché</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper-warm"><X size={18} className="text-ink-faint" /></button>
        </div>

        <div className="space-y-4">
          <Field label="Nom du marché" error={errors.name}>
            <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Ex: Plombier Lorient" className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold" />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Pays">
              <select value={form.country} onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold">
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Langue">
              <select value={form.language} onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold">
                {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
            <Field label="Devise">
              <select value={form.currency} onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Mot-clé graine" error={errors.seed_keyword}>
            <input type="text" value={form.seed_keyword} onChange={(e) => setForm((p) => ({ ...p, seed_keyword: e.target.value }))}
              placeholder="Ex: plombier lorient" className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-ink hover:bg-paper-warm rounded-lg">Annuler</button>
          <button onClick={handleSubmit} disabled={create.isPending}
            className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg disabled:opacity-50">
            {create.isPending && <Loader2 size={14} className="animate-spin" />}
            Créer le marché
          </button>
        </div>
      </div>
    </div>
  )
}
